import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  signal,
  computed,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

type DateValue = Date | string | null;

interface CalendarDay {
  date: Date;
  day: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
}

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
})
export class DatePickerComponent implements ControlValueAccessor, OnInit {
  @Input() placeholder = 'Select date';
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() disabled = false;
  @Input() required = false;
  @Input() label?: string;
  @Input() errorMessage?: string;
  @Input() hasError = false;
  @Input() defaultToToday = false;

  @Output() dateChange = new EventEmitter<Date | null>();

  // Internal state
  private readonly value = signal<Date | null>(null);
  private readonly currentViewDate = signal<Date>(new Date());

  ngOnInit() {
    // Set today's date as default if specified
    if (this.defaultToToday) {
      const today = new Date();
      this.value.set(today);
      const formattedDate = this.formatDate(today);
      this.onChange(formattedDate);
      this.dateChange.emit(today);
    }
  }
  protected isCalendarOpen = signal<boolean>(false);

  // Control Value Accessor
  private onChange = (_value: DateValue) => {};
  private onTouched = () => {};

  // Days of week labels
  readonly daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  // Computed values
  displayValue = computed(() => {
    const date = this.value();
    return date ? this.formatDate(date) : '';
  });

  currentMonthYear = computed(() => {
    const date = this.currentViewDate();
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  });

  currentMonth = computed(() => {
    const date = this.currentViewDate();
    return date.toLocaleDateString('en-US', { month: 'long' });
  });

  currentYear = computed(() => {
    return this.currentViewDate().getFullYear();
  });

  availableYears = computed(() => {
    const currentYear = new Date().getFullYear();
    const minYear = this.minDate ? this.minDate.getFullYear() : currentYear - 100;
    // Limit to current year + 1 only
    const maxYear = this.maxDate ? this.maxDate.getFullYear() : currentYear + 1;

    const years: number[] = [];
    for (let year = minYear; year <= maxYear; year++) {
      years.push(year);
    }
    return years;
  });

  canNavigateToNextYear = computed(() => {
    const currentYear = new Date().getFullYear();
    const maxYear = this.maxDate ? this.maxDate.getFullYear() : currentYear + 1;
    return this.currentYear() < maxYear;
  });

  canNavigateToPreviousYear = computed(() => {
    const currentYear = new Date().getFullYear();
    const minYear = this.minDate ? this.minDate.getFullYear() : currentYear - 100;
    return this.currentYear() > minYear;
  });

  inputClasses = computed(() => {
    const baseClasses =
      'w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none text-sm transition-colors';
    const stateClasses = this.disabled
      ? 'bg-gray-50 text-gray-500 cursor-not-allowed border-gray-200'
      : 'bg-white text-gray-900 cursor-pointer border-gray-300 hover:border-primary';

    return `${baseClasses} ${stateClasses}`;
  });

  calendarWeeks = computed(() => {
    const viewDate = this.currentViewDate();
    const selectedDate = this.value();
    const today = new Date();

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    // Get first day of the month
    const firstDayOfMonth = new Date(year, month, 1);
    const _lastDayOfMonth = new Date(year, month + 1, 0);

    // Get first day of the calendar (might be from previous month)
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - firstDayOfMonth.getDay());

    const weeks: CalendarDay[][] = [];
    const currentDate = new Date(startDate);

    for (let week = 0; week < 6; week++) {
      const weekDays: CalendarDay[] = [];

      for (let day = 0; day < 7; day++) {
        const isCurrentMonth = currentDate.getMonth() === month;
        const isToday = this.isSameDay(currentDate, today);
        const isSelected = selectedDate && this.isSameDay(currentDate, selectedDate);
        const isDisabled = this.isDateDisabled(currentDate);

        weekDays.push({
          date: new Date(currentDate),
          day: currentDate.getDate(),
          isCurrentMonth,
          isToday,
          isSelected: !!isSelected,
          isDisabled,
        });

        currentDate.setDate(currentDate.getDate() + 1);
      }

      weeks.push(weekDays);

      // Stop if we've shown all days of the current month and we're starting a new month
      if (currentDate.getMonth() !== month && week >= 4) {
        break;
      }
    }

    return weeks;
  });

  toggleCalendar() {
    if (this.disabled) {
      return;
    }
    this.isCalendarOpen.update((open) => !open);
  }

  closeCalendar() {
    this.isCalendarOpen.set(false);
  }

  selectDate(day: CalendarDay) {
    if (day.isDisabled) {
      return;
    }

    this.value.set(day.date);
    const formattedDate = this.formatDate(day.date);
    this.onChange(formattedDate);
    this.onTouched();
    this.dateChange.emit(day.date);
    this.closeCalendar();
  }

  selectToday() {
    const today = new Date();
    if (this.isDateDisabled(today)) {
      return;
    }

    this.value.set(today);
    this.currentViewDate.set(today);
    const formattedDate = this.formatDate(today);
    this.onChange(formattedDate);
    this.onTouched();
    this.dateChange.emit(today);
    this.closeCalendar();
  }

  previousMonth() {
    this.currentViewDate.update((date) => {
      const newDate = new Date(date);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  }

  nextMonth() {
    this.currentViewDate.update((date) => {
      const newDate = new Date(date);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  }

  previousYear() {
    this.currentViewDate.update((date) => {
      const newDate = new Date(date);
      newDate.setFullYear(newDate.getFullYear() - 1);
      return newDate;
    });
  }

  nextYear() {
    this.currentViewDate.update((date) => {
      const newDate = new Date(date);
      newDate.setFullYear(newDate.getFullYear() + 1);
      return newDate;
    });
  }

  selectYear(year: number) {
    this.currentViewDate.update((date) => {
      const newDate = new Date(date);
      newDate.setFullYear(year);
      return newDate;
    });
  }

  getDayClasses(day: CalendarDay): string {
    const baseClasses =
      'w-8 h-8 flex items-center justify-center text-sm rounded-lg transition-all duration-200';

    if (day.isDisabled) {
      return `${baseClasses} text-gray-300 cursor-not-allowed`;
    }

    if (day.isSelected) {
      return `${baseClasses} bg-primary text-white font-semibold shadow-md`;
    }

    if (day.isToday) {
      return `${baseClasses} bg-primary text-white font-semibold border-2 border-primary shadow-lg ring-2 ring-primary ring-opacity-50`;
    }

    if (!day.isCurrentMonth) {
      return `${baseClasses} text-gray-400 hover:bg-gray-100`;
    }

    return `${baseClasses} text-gray-700 hover:bg-primary hover:text-white`;
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  private isDateDisabled(date: Date): boolean {
    if (this.minDate && date < this.minDate) {
      return true;
    }
    if (this.maxDate && date > this.maxDate) {
      return true;
    }
    return false;
  }

  // ControlValueAccessor implementation
  writeValue(value: DateValue): void {
    if (value) {
      const date = typeof value === 'string' ? new Date(value) : value;
      this.value.set(date);
      this.currentViewDate.set(date);
    } else {
      this.value.set(null);
      // Reset to current date when value is cleared
      this.currentViewDate.set(new Date());
    }
  }

  registerOnChange(fn: (value: DateValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
